import classNames from 'classnames';
import React, {
  forwardRef, useEffect, useImperativeHandle, useRef,
} from 'react';

import { onlyDigits, PASSWORD_LENGTH } from '@/utils/password';

import styles from './PinInputs.module.scss';

export interface PinInputsHandle {
  focus: () => void;
}

interface PinInputsProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onComplete?: () => void;
  onEnter?: () => void;
  status?: 'error' | 'success';
  autoFocus?: boolean;
}

const POSITIONS = Array.from({ length: PASSWORD_LENGTH }, (_, index) => index);

const PinInputs = forwardRef<PinInputsHandle, PinInputsProps>(({
  id,
  value,
  onChange,
  onComplete,
  onEnter,
  status,
  autoFocus = false,
}, ref) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // Valor actual sin esperar al re-render del padre: el onFocus de la casilla a la que
  // saltamos se ejecuta antes de que llegue el nuevo `value` por props
  const valueRef = useRef<string>(value);
  valueRef.current = value;

  // Siempre se escribe en la primera casilla vacía para que la clave quede contigua
  const focusNext = (length: number) => {
    inputsRef.current[Math.min(length, PASSWORD_LENGTH - 1)]?.focus();
  };

  useImperativeHandle(ref, () => ({ focus: () => focusNext(valueRef.current.length) }));

  useEffect(() => {
    if (autoFocus) {
      focusNext(valueRef.current.length);
    }
  }, []);

  const setValue = (newValue: string) => {
    const previousLength = valueRef.current.length;
    valueRef.current = newValue;
    onChange(newValue);
    focusNext(newValue.length);
    if (newValue.length === PASSWORD_LENGTH && previousLength < PASSWORD_LENGTH) {
      onComplete?.();
    }
  };

  const deleteLast = () => {
    setValue(valueRef.current.slice(0, -1));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = onlyDigits(event.target.value);
    if (digits) {
      setValue(onlyDigits(valueRef.current + digits));
    } else if (event.target.value === '') {
      // Algunos teclados Android no informan de la tecla Backspace en keydown
      deleteLast();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    setValue(onlyDigits(valueRef.current + event.clipboardData.getData('text')));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      deleteLast();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onEnter?.();
    }
  };

  return (
    <div className={styles.pinInputs}>
      {POSITIONS.map((index) => (
        <div
          key={index}
          className={classNames(
            styles.inputWrapper,
            value[index] !== undefined && styles.withAsterisk,
          )}
        >
          <input
            id={`${id}-${index + 1}`}
            ref={(element) => { inputsRef.current[index] = element; }}
            className={classNames(
              styles.input,
              status === 'error' && styles.error,
              status === 'success' && styles.success,
            )}
            // type="text" para que el navegador no ofrezca el gestor de contraseñas
            // (ignora autocomplete="off" en type="password"); el dígito se oculta por CSS
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
            maxLength={1}
            value={value[index] ?? ''}
            onFocus={() => focusNext(valueRef.current.length)}
            onChange={handleChange}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
          />
        </div>
      ))}
    </div>
  );
});

export default PinInputs;
